import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Autocomplete,
  Box,
  Button,
  debounce,
  Dialog,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { useAppSelector } from "../../../../../../hooks/store-hooks";
import { useEffect, useState } from "react";
import {
  useListaCEPService,
  useLocalizacaoService,
  useProjetosService,
} from "../../../../../../../use-cases/geo-veles";
import { Transition } from "../../../../../../components";
import { ILocalizacao } from "../../../../../../../application/models";
import { ListasCEP } from "./lista-cep";
import { PreviaFiltroLocalizacao } from "./previa-filtro-localizacao";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { theme } from "../../../../../../styles";
import { ListasCidades } from "./lista-cidades";

export const categoriaValueToLabel = (value: keyof ILocalizacao) => {
  switch (value) {
    case "macrorregiao":
      return "Macrorregião";
    case "mesorregiao":
      return "Mesorregião";
    case "uf":
      return "Estado";
    case "cidade":
      return "Cidade";
    case "listasCidade":
      return "Listas de Cidades";
    case "bairro":
      return "Bairro";
    case "cep":
      return "CEP";

    default:
      return "Macrorregião";
  }
};

export const categoriaLocalizacaoOptions = [
  {
    label: "Bairro",
    value: "bairro",
  },
  {
    label: "CEP",
    value: "cep",
  },
  {
    label: "Cidade",
    value: "cidade",
  },
  {
    label: " Listas de Cidades",
    value: "listasCidade",
  },
  {
    label: "Estado",
    value: "uf",
  },
  {
    label: "Macrorregião",
    value: "macrorregiao",
  },
  {
    label: "Mesorregião",
    value: "mesorregiao",
  },
];

export const LocalizacaoDialog = () => {
  // State
  const { projetos, localizacao } = useAppSelector((state) => state);
  // Use State
  const [purge, setPurge] = useState<boolean>(false);
  const [cepSearchTerm, setCepSearchTerm] = useState<string>("");
  const [cidadeSearchTerm, setcidadeSearchTerm] = useState<string>("");

  // Hook
  const {
    setCategoria,
    setSearchTerm,
    resetLocalizacoesList,
    searchLocalizacao,
    openLocalizacaoDialog,
  } = useLocalizacaoService(); // Adicione fetchCepList aqui
  const { addLocalizacaoFilter, addPurgeLocalizacaoFilter } =
    useProjetosService();

  const { setupListaCEP } = useListaCEPService();

  const handleLocalizacaoFilterClose = () => {
    openLocalizacaoDialog(!localizacao.openLocalizacaoDialog);
    setSearchTerm("");
    setCategoria("macrorregiao");
    // setPurge(false)
    resetLocalizacoesList();
  };

  useEffect(() => {
    searchLocalizacao();
  }, [localizacao.searchTerm, searchLocalizacao]);

  // USE EFFECT PARA LISTAS DE CEP
  useEffect(() => {
    if (localizacao.categoria === "cep") {
      setupListaCEP(); // Chama a função que busca a lista de CEPs
    }
  }, [localizacao.categoria, setupListaCEP]); // Monitora a mudança na categoria

  return (
    <>
      <Dialog
        fullScreen
        open={localizacao.openLocalizacaoDialog}
        TransitionComponent={Transition}
        onClose={() => {
          handleLocalizacaoFilterClose();
        }}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography
              sx={{ ml: 2, flex: 1 }}
              textTransform="uppercase"
              variant="h6"
              component="div"
            >
              Selecionar localização
            </Typography>
            <Button
              autoFocus
              color="inherit"
              onClick={() => {
                handleLocalizacaoFilterClose();
              }}
            >
              Fechar
            </Button>
          </Toolbar>
        </AppBar>
        <Box p={3}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              searchLocalizacao();
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={3}>
                <Autocomplete
                  options={categoriaLocalizacaoOptions}
                  onChange={(
                    event: any,
                    newValue: { label: string; value: string } | null
                  ) => {
                    if (newValue === null) {
                      return;
                    }
                    setCategoria(newValue?.value as keyof ILocalizacao);
                    setSearchTerm("");
                    resetLocalizacoesList();
                  }}
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      label="Selecione o tipo de localização"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={2}>
                <FormControlLabel
                  label={purge ? "Não incluir" : "Incluir"}
                  control={
                    <Switch
                      checked={purge}
                      onChange={(e) => {
                        setPurge(e.target.checked);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  }
                />
              </Grid>
              <Grid item xs={7}>
                <TextField
                  fullWidth
                  label={
                    localizacao.categoria !== "cep"
                      ? "Digite o nome da localização"
                      : "Digite o nome da lista"
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  onChange={debounce((e) => {
                    if (localizacao.categoria !== "cep") {
                      setSearchTerm(e.target.value);
                    } else {
                      setCepSearchTerm(e.target.value);
                    }
                  }, 500)}
                />
              </Grid>
            </Grid>
          </form>
        </Box>
        {localizacao.categoria === "cep" ? (
          <ListasCEP purge={purge} searchTerm={cepSearchTerm} />
        ) : localizacao.categoria === "listasCidade" ? (
          <ListasCidades purge={purge} searchTerm={cidadeSearchTerm} />
        ) : (
          <Grid container>
            <Grid item xs={6}>
              <List>
                {localizacao.localizacoes.map((localizacao, index) => {
                  if (
                    projetos?.filtrosSelecionados?.localizacao?.includes(
                      localizacao
                    ) ||
                    projetos?.filtrosSelecionados?.purgeLocalizacao?.includes(
                      localizacao
                    )
                  ) {
                    return false;
                  }

                  if (localizacao.categoria === "bairro") {
                    if (
                      projetos?.filtrosSelecionados?.localizacao?.find(
                        (selectedLocalizacao) =>
                          selectedLocalizacao.bairro === localizacao.bairro &&
                          selectedLocalizacao.cidade === localizacao.cidade
                      ) ||
                      projetos?.filtrosSelecionados?.purgeLocalizacao?.find(
                        (purgeLocalizacao) =>
                          purgeLocalizacao.bairro === localizacao.bairro &&
                          purgeLocalizacao.cidade === localizacao.cidade
                      )
                    ) {
                      return false;
                    }
                  }

                  if (localizacao.categoria === "macrorregiao") {
                    if (
                      projetos?.filtrosSelecionados?.localizacao?.find(
                        (selectedLocalizacao) =>
                          selectedLocalizacao.categoria === "macrorregiao" &&
                          selectedLocalizacao.macrorregiao ===
                            localizacao.macrorregiao
                      ) ||
                      projetos?.filtrosSelecionados?.purgeLocalizacao?.find(
                        (purgeLocalizacao) =>
                          purgeLocalizacao.categoria === "macrorregiao" &&
                          purgeLocalizacao.macrorregiao ===
                            localizacao.macrorregiao
                      )
                    ) {
                      return false;
                    }
                  }

                  if (localizacao.categoria === "mesorregiao") {
                    if (
                      projetos?.filtrosSelecionados?.localizacao?.find(
                        (selectedLocalizacao) =>
                          selectedLocalizacao.categoria === "mesorregiao" &&
                          selectedLocalizacao.mesorregiao ===
                            localizacao.mesorregiao
                      ) ||
                      projetos?.filtrosSelecionados?.purgeLocalizacao?.find(
                        (purgeLocalizacao) =>
                          purgeLocalizacao.categoria === "mesorregiao" &&
                          purgeLocalizacao.mesorregiao ===
                            localizacao.mesorregiao
                      )
                    ) {
                      return false;
                    }
                  }

                  if (localizacao.categoria === "uf") {
                    if (
                      projetos?.filtrosSelecionados?.localizacao?.find(
                        (selectedLocalizacao) =>
                          selectedLocalizacao.categoria === "uf" &&
                          selectedLocalizacao.uf === localizacao.uf
                      ) ||
                      projetos?.filtrosSelecionados?.purgeLocalizacao?.find(
                        (purgeLocalizacao) =>
                          purgeLocalizacao.categoria === "uf" &&
                          purgeLocalizacao.uf === localizacao.uf
                      )
                    ) {
                      return false;
                    }
                  }

                  if (localizacao.categoria === "cidade") {
                    
                    if (
                      projetos?.filtrosSelecionados?.localizacao?.find(
                        (selectedLocalizacao) =>
                          selectedLocalizacao.cidade === localizacao.cidade &&
                          selectedLocalizacao.uf === localizacao.uf
                      ) ||
                      projetos?.filtrosSelecionados?.purgeLocalizacao?.find(
                        (purgeLocalizacao) =>
                          purgeLocalizacao.cidade === localizacao.cidade &&
                          purgeLocalizacao.uf === localizacao.uf
                      )
                    ) {
                      return false;
                    }
                  }

                  return (
                    <>
                      <ListItem
                        button
                        key={localizacao.value}
                        onClick={() => {
                          if (purge) {
                            addPurgeLocalizacaoFilter(localizacao);
                          } else {
                            addLocalizacaoFilter(localizacao);
                          }
                        }}
                      >
                        <ListItemText
                          primary={localizacao.value?.toUpperCase()}
                          secondary={categoriaValueToLabel(
                            localizacao.categoria as keyof ILocalizacao
                          ).toUpperCase()}
                        />
                      </ListItem>
                      <Divider color={theme.palette.primary.main} />
                    </>
                  );
                })}
              </List>
            </Grid>
            {((projetos?.filtrosSelecionados?.localizacao?.length ?? 0) > 0 ||
              (projetos?.filtrosSelecionados?.purgeLocalizacao?.length ?? 0) >
                0) && (
              <Grid item xs={6}>
                <Box p={3} my={3}>
                  <Accordion defaultExpanded>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1a-content"
                      id="panel1a-header"
                    >
                      <Typography
                        variant="h5"
                        color="primary"
                        fontWeight="bold"
                        textTransform="uppercase"
                      >
                        Localizações selecionadas
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      
                      <PreviaFiltroLocalizacao />
                      
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Dialog>
    </>
  );
};
